export const common = {
  nav: {
    home: 'Home',
    features: 'Features',
    cases: 'AI Cases',
    workflow: 'Workflow',
    contact: 'Contact',
    requestDemo: 'Demo',
    demo: 'Demo',
    predictions: 'Predictions',
  },
  hero: {
    title: 'AI-Powered Medical Imaging Analysis',
    subtitle: 'Comprehensive intelligent medical imaging system for automatic analysis of multiple conditions including fractures, osteoarthritis, lung abnormalities, scoliosis, and dental conditions',
    cta: 'Get Started',
    fdaCompliant: 'FDA Compliant',
    hipaaSecure: 'HIPAA Secure',
    confidence: 'Confidence',
    detected: 'Detected',
  },
  features: {
    label: 'CORE CAPABILITIES',
    title: 'Advanced Multi-Case Detection Technology',
    subtitle: 'Leveraging cutting-edge AI and computer vision to deliver fast, accurate, and comprehensive medical imaging analysis across multiple medical conditions for healthcare professionals worldwide.',
    
    fracture: {
      title: 'Bone Fracture Detection',
      description: 'Accurately detects multiple types of bone fractures including elbow, wrist, shoulder, humerus, forearm, and finger fractures with precise localization.',
      metric: '6 Fracture Types',
    },
    knee: {
      title: 'Knee Osteoarthritis Detection',
      description: 'Advanced analysis of knee joint degeneration, identifying osteoarthritis severity levels and providing early detection of cartilage deterioration.',
      metric: '5 Severity Grades',
    },
    lung: {
      title: 'Lung Abnormalities Detection',
      description: 'Comprehensive chest X-ray analysis detecting atelectasis, cardiomegaly, pleural effusion, infiltrates, nodules, masses, pneumonia, and pneumothorax with high accuracy.',
      metric: '8 Conditions',
    },
    scoliosis: {
      title: 'Scoliosis Detection',
      description: 'Identifies spinal curvature abnormalities and severity levels from X-ray images.',
      metric: '4 Spinal Regions',
    },
    teeth: {
      title: 'Teeth Detection',
      description: 'Detects all dental conditions including Caries, Crown, Filling, Implant, Bone Loss, and Fracture Teeth using X-ray images with YOLOv8 model.',
      metric: '31 Dental Conditions',
    },
    cavity: {
      title: 'Cavity Detection',
      description: 'Focuses specifically on detecting cavities versus normal teeth using real clinical images with YOLOv8 model. Supports JPEG/PNG with bounding box visualization.',
      metric: 'Real-time Detection',
    },
    annotated: {
      title: 'Annotated Images',
      description: 'Get detailed visual annotations highlighting detected conditions with bounding boxes and labels.',
      metric: 'Visual Clarity',
    },
    api: {
      title: 'High-Performance API',
      description: 'Built with FastAPI for fast, scalable performance and easy integration with web, mobile, and hospital systems.',
      metric: 'Fast Integration',
    },
  },
  cases: {
    badge: 'Clinical Applications',
    title: 'Comprehensive Medical Imaging Solutions',
    subtitle: 'Advanced AI-powered detection systems covering multiple medical specialties with clinical-grade accuracy and real-time analysis capabilities.',
    detectionTypes: 'Detection Types',
    keyFeatures: 'Key Features',
    aiDetection: 'AI Detection Active',
    tryDemo: 'Try Interactive Demo',
    
    // Detection type translations
    detectionTypeLabels: {
      // Fracture types
      elbow: 'Elbow',
      wrist: 'Wrist',
      shoulder: 'Shoulder',
      humerus: 'Humerus',
      forearm: 'Forearm',
      finger: 'Finger',
      
      // Knee grades
      normal: 'Normal',
      doubtful: 'Doubtful',
      minimal: 'Minimal',
      moderate: 'Moderate',
      severe: 'Severe',
      
      // Lung conditions
      atelectasis: 'Atelectasis',
      cardiomegaly: 'Cardiomegaly',
      effusion: 'Pleural Effusion',
      infiltrates: 'Infiltrates',
      nodules: 'Nodules',
      masses: 'Masses',
      pneumonia: 'Pneumonia',
      pneumothorax: 'Pneumothorax',
      
      // Dental conditions
      caries: 'Caries',
      crown: 'Crown',
      filling: 'Filling',
      implant: 'Implant',
      boneLoss: 'Bone Loss',
      fracture: 'Fracture',
      
      // Cavity detection
      cavity: 'Cavity',
      normalTooth: 'Normal Tooth',
      
      // Scoliosis regions
      cervical: 'Cervical',
      thoracic: 'Thoracic',
      lumbar: 'Lumbar',
      sacral: 'Sacral',
    },
    
    fracture: {
      title: 'Bone Fracture Detection',
      description: 'YOLO-based real-time fracture detection system covering multiple anatomical regions with high accuracy and clinical recommendations.',
      detectionLabel: 'Wrist Fracture Detected',
      feature1Title: 'Instant Detection',
      feature1Desc: 'Immediate fracture identification with location pinpointing and confidence scores.',
      feature2Title: 'Multi-Region Coverage',
      feature2Desc: 'Comprehensive analysis across 6 major anatomical regions including upper and lower extremities.',
      feature3Title: 'Clinical Integration',
      feature3Desc: 'Seamless PACS integration with automated reporting and treatment recommendations.',
    },
    
    knee: {
      title: 'Knee Osteoarthritis Detection',
      description: 'EfficientNet-B5 powered classification system that categorizes knee osteoarthritis into 5 severity grades for precise treatment planning.',
      detectionLabel: 'Grade 3 Moderate OA',
      feature1Title: '5-Grade Classification',
      feature1Desc: 'Precise severity assessment from Normal to Severe using validated clinical grading systems.',
      feature2Title: 'Progression Tracking',
      feature2Desc: 'Longitudinal analysis to monitor disease progression and treatment effectiveness over time.',
      feature3Title: 'Treatment Guidance',
      feature3Desc: 'Evidence-based recommendations for conservative or surgical interventions based on severity.',
    },
    
    teeth: {
      title: 'Dental Condition Detection',
      description: 'Comprehensive dental AI system detecting 31 different conditions from panoramic X-rays for complete oral health assessment.',
      detectionLabel: 'Multiple Conditions Detected',
      feature1Title: '31 Condition Types',
      feature1Desc: 'Complete coverage of dental pathologies, restorations, and orthodontic appliances in a single scan.',
      feature2Title: 'Panoramic Analysis',
      feature2Desc: 'Full-mouth assessment with precise localization of each detected condition and tooth numbering.',
      feature3Title: 'Treatment Planning',
      feature3Desc: 'Automated dental charting and comprehensive treatment plan generation for efficient workflows.',
    },
    
    scoliosis: {
      title: 'Scoliosis Detection',
      description: 'Advanced spinal curvature analysis system covering 4 spinal regions with Cobb angle measurement for accurate diagnosis.',
      detectionLabel: 'Thoracic Curvature 28°',
      feature1Title: 'Cobb Angle Measurement',
      feature1Desc: 'Automated calculation of spinal curvature angles with high precision and reproducibility.',
      feature2Title: '4 Spinal Regions',
      feature2Desc: 'Complete analysis of Cervical, Thoracic, Lumbar, and Sacral regions for comprehensive assessment.',
      feature3Title: 'Severity Classification',
      feature3Desc: 'Automatic grading from mild to severe with treatment recommendations and monitoring protocols.',
    },
  },
  workflow: {
    label: 'HOW IT WORKS',
    title: 'AI-Powered Analysis Pipeline',
    steps: {
      step1: {
        title: 'Upload Medical Image',
        description: 'User submits X-ray or medical image',
      },
      step2: {
        title: 'AI Detection',
        description: 'Model analyzes and detects conditions',
      },
      step3: {
        title: 'Generate Results',
        description: 'Confidence scores and annotations',
      },
      step4: {
        title: 'Detailed Analysis',
        description: 'Comprehensive condition assessment',
      },
      step5: {
        title: 'Treatment Guide',
        description: 'Recommendations generated',
      },
      step6: {
        title: 'Return Results',
        description: 'Annotated image and JSON response',
      },
    },
    stats: {
      analysisTime: 'Analysis Time',
      accuracyRate: 'Accuracy Rate',
      automatedService: 'Automated Service',
    },
  },
  cta: {
    title: 'Ready to Transform Your Diagnostic Workflow?',
    subtitle1: 'Join 500+ healthcare facilities',
    subtitle2: 'using AI-powered medical imaging analysis',
    button: 'Request Demo',
  },
  demo: {
    badge: 'Interactive Demo',
    title: 'See It In Action',
    subtitle: 'Experience the power of AI-driven medical imaging analysis across multiple conditions',
    selectCase: 'Select AI Case',
    uploadTitle: 'Upload Medical Image',
    uploadDesc: 'Drag and drop or click to upload',
    fileFormats: 'PNG, JPG, DICOM',
    minSize: 'Min 224x224px',
    sampleImage: 'Sample Image',
    analyzeButton: 'Analyze Image',
    analyzing: 'Analyzing...',
    results: 'Analysis Results',
    aiOutput: 'AI Analysis Output',
    confidence: 'Confidence',
    location: 'Location',
    condition: 'Condition',
    tryDemo: 'Try Demo',
    readyToAnalyze: 'Ready to Analyze',
    readyToAnalyzeDesc: 'Upload a medical image and click "Analyze Image" to see AI-powered detection results',
    cases: {
      fracture: 'Fracture Detection',
      knee: 'Knee Osteoarthritis',
      lung: 'Lung Abnormalities',
      scoliosis: 'Scoliosis Detection',
      teeth: 'Dental Conditions',
      cavity: 'Cavity Detection',
    },
  },
  contact: {
    title: 'Get in Touch',
    subtitle: 'Have questions? We\'re here to help you integrate our AI-powered solution',
    name: 'Full Name',
    email: 'Email Address',
    message: 'Your Message',
    send: 'Send Message',
    sendWhatsApp: 'Send via WhatsApp',
    info: {
      title: 'Contact Information',
      email: 'info@5vix.com',
      phone: '+20 1044553254',
    },
    businessHours: {
      title: 'Business Hours',
      weekdays: 'Monday - Friday: 9:00 AM - 6:00 PM',
      saturday: 'Saturday: 10:00 AM - 4:00 PM',
      sunday: 'Sunday: Closed',
    },
  },
  footer: {
    newsletter: {
      title: 'Stay Updated',
      subtitle: 'Get the latest updates on medical AI technology',
      placeholder: 'Type your message',
      button: 'Subscribe',
    },
    sendWhatsApp: 'Send via WhatsApp',
    quickLinks: 'Quick Links',
    contact: 'Contact Us',
    copyright: '© 2025 5vix. All rights reserved.',
    privacy: 'Privacy Policy',
    terms: 'Terms of Service',
    poweredBy: 'Powered by Debug',
  },
  predictions: {
    badge: 'AI Predictions',
    pageTitle: 'Medical AI Prediction Tools',
    pageSubtitle: 'Advanced AI-powered clinical decision support tools for healthcare professionals. Enter numerical data for instant analysis.',
    disclaimer: {
      title: 'Medical Decision Support Only',
      text: 'These AI predictions are intended to assist healthcare professionals and should not replace professional medical diagnosis. Always consult with qualified medical personnel for final diagnosis and treatment decisions.',
    },
    common: {
      analyze: 'Analyze Data',
      analyzing: 'Analyzing...',
      results: 'Prediction Results',
      waitingForInput: 'Enter patient data and click analyze to see AI predictions',
      processingAI: 'AI model processing data...',
      primaryPrediction: 'Primary Prediction',
      allProbabilities: 'All Condition Probabilities',
      confidence: 'Confidence',
      yes: 'Yes',
      no: 'No',
    },
    heart: {
      title: 'Heart Condition (ECG)',
      subtitle: 'Predict heart conditions based on ECG-related numerical data',
      inputTitle: 'Enter ECG & Health Data',
      fields: {
        age: 'Age (years)',
        restingBP: 'Resting BP (mmHg)',
        cholesterol: 'Cholesterol (mg/dL)',
        fastingBS: 'Fasting Blood Sugar',
        maxHR: 'Max Heart Rate',
        oldpeak: 'ST Depression',
        restingECG: 'Resting ECG',
        chestPain: 'Chest Pain Type',
        exerciseAngina: 'Exercise Angina',
        stSlope: 'ST Slope',
      },
      options: {
        ecgNormal: 'Normal',
        ecgST: 'ST-T Abnormality',
        ecgLVH: 'Left Ventricular Hypertrophy',
        painTypical: 'Typical Angina',
        painAtypical: 'Atypical Angina',
        painNonAnginal: 'Non-Anginal Pain',
        painAsymptomatic: 'Asymptomatic',
        slopeUp: 'Upsloping',
        slopeFlat: 'Flat',
        slopeDown: 'Downsloping',
      },
      conditions: {
        normal: 'Normal Sinus Rhythm',
        arrhythmia: 'Cardiac Arrhythmia',
        heartDisease: 'Coronary Heart Disease',
        hypertension: 'Hypertensive Heart',
        other: 'Other Conditions',
      },
    },
    diabetes: {
      title: 'Diabetes Prediction',
      subtitle: 'Determine diabetes risk based on health indicators',
      inputTitle: 'Enter Health Indicators',
      fields: {
        pregnancies: 'Pregnancies',
        glucose: 'Glucose (mg/dL)',
        bloodPressure: 'Blood Pressure',
        skinThickness: 'Skin Thickness',
        insulin: 'Insulin (µU/mL)',
        bmi: 'BMI',
        diabetesPedigree: 'Diabetes Pedigree',
        age: 'Age (years)',
      },
      results: {
        diabetic: 'Diabetic',
        nonDiabetic: 'Non-Diabetic',
        riskLevel: 'Risk Level',
        risk: {
          low: 'Low Risk',
          moderate: 'Moderate Risk',
          high: 'High Risk',
        },
        recommendation: 'Recommendation',
        recommendDiabetic: 'Consult an endocrinologist for comprehensive diabetes management and lifestyle modifications.',
        recommendHealthy: 'Maintain healthy lifestyle habits and schedule regular health checkups.',
      },
    },
    anemia: {
      title: 'Anemia Prediction',
      subtitle: 'Predict anemia type based on blood parameters and iron studies',
      inputTitle: 'Enter Anemia Parameters',
      basicParams: 'Basic Parameters',
      rbcIndices: 'RBC Indices',
      ironStudies: 'Iron Studies',
      vitamins: 'Vitamin Levels',
      fields: {
        hemoglobin: 'Hemoglobin',
        mcv: 'MCV',
        mch: 'MCH',
        mchc: 'MCHC',
        rdw: 'RDW',
        ferritin: 'Ferritin',
        iron: 'Serum Iron',
        tibc: 'TIBC',
        vitaminB12: 'Vitamin B12',
        folate: 'Folate',
      },
      severity: 'Severity',
      severityLevels: {
        normal: 'Normal',
        mild: 'Mild',
        moderate: 'Moderate',
        severe: 'Severe',
      },
      conditions: {
        normal: 'No Anemia Detected',
        ironDeficiency: 'Iron Deficiency Anemia',
        vitaminB12: 'Vitamin B12 Deficiency Anemia',
        folateDeficiency: 'Folate Deficiency Anemia',
        chronicDisease: 'Anemia of Chronic Disease',
        thalassemia: 'Thalassemia',
        hemolytic: 'Hemolytic Anemia',
        sideroblastic: 'Sideroblastic Anemia',
        myelodysplastic: 'Myelodysplastic Syndrome',
        alcoholRelated: 'Alcohol-Related Anemia',
        aplastic: 'Aplastic Anemia',
      },
      clinicalNote: 'Clinical Note',
      clinicalNoteText: 'This prediction identifies the most likely type of anemia. Confirm with peripheral blood smear, reticulocyte count, and additional specialized tests.',
    },
    cbc: {
      title: 'CBC Analysis',
      subtitle: 'Comprehensive Complete Blood Count interpretation',
      inputTitle: 'Enter CBC Values',
      rbcParams: 'Red Blood Cell Parameters',
      rbcIndices: 'RBC Indices',
      wbcPlatelets: 'WBC & Platelets',
      differential: 'WBC Differential',
      fields: {
        rbc: 'RBC Count',
        hemoglobin: 'Hemoglobin',
        hematocrit: 'Hematocrit',
        mcv: 'MCV',
        mch: 'MCH',
        mchc: 'MCHC',
        rdw: 'RDW',
        wbc: 'WBC Count',
        platelets: 'Platelets',
        neutrophils: 'Neutrophils',
        lymphocytes: 'Lymphocytes',
        monocytes: 'Monocytes',
      },
      overallStatus: 'Overall Status',
      statusNormal: 'All Values Within Normal Range',
      statusAbnormal: 'Abnormal Values Detected',
      abnormalParams: 'parameters outside normal range',
      parameterResults: 'Parameter Analysis',
      ref: 'Ref',
      status: {
        low: 'Low',
        normal: 'Normal',
        high: 'High',
      },
      clinicalNote: 'Clinical Note',
      clinicalNoteText: 'This CBC analysis compares values against standard reference ranges. Abnormal results should be correlated with clinical findings and may require further investigation.',
    },
    kidney: {
      title: 'Kidney Function',
      subtitle: 'Predict kidney disease stage using creatinine, BUN, and GFR values',
      inputTitle: 'Enter Kidney Function Parameters',
      coreParams: 'Core Parameters',
      electrolytes: 'Electrolytes',
      additional: 'Additional Parameters',
      fields: {
        creatinine: 'Creatinine',
        bun: 'BUN',
        gfr: 'eGFR',
        sodium: 'Sodium',
        potassium: 'Potassium',
        calcium: 'Calcium',
        phosphorus: 'Phosphorus',
        albumin: 'Albumin',
        uricAcid: 'Uric Acid',
        age: 'Age (years)',
      },
      severity: 'Severity',
      severityLevels: {
        normal: 'Normal',
        mild: 'Mild',
        moderate: 'Moderate',
        severe: 'Severe',
      },
      conditions: {
        normal: 'Normal Kidney Function',
        stage1: 'CKD Stage 1 - Kidney Damage with Normal GFR',
        stage2: 'CKD Stage 2 - Mild Decrease in GFR',
        stage3: 'CKD Stage 3 - Moderate Decrease in GFR',
        stage4: 'CKD Stage 4 - Severe Decrease in GFR',
        stage5: 'CKD Stage 5 - Kidney Failure',
        aki: 'Acute Kidney Injury',
      },
      clinicalNote: 'Clinical Note',
      clinicalNoteText: 'This prediction estimates CKD staging based on GFR and kidney markers. Confirm with urinalysis, kidney ultrasound, and serial measurements over 3 months for definitive diagnosis.',
    },
    liver: {
      title: 'Liver Function',
      subtitle: 'Analyze liver function tests to identify potential liver conditions',
      inputTitle: 'Enter Liver Function Parameters',
      enzymes: 'Liver Enzymes',
      bilirubin: 'Bilirubin',
      proteins: 'Proteins & Coagulation',
      fields: {
        alt: 'ALT (SGPT)',
        ast: 'AST (SGOT)',
        alp: 'ALP',
        ggt: 'GGT',
        totalBilirubin: 'Total Bilirubin',
        directBilirubin: 'Direct Bilirubin',
        albumin: 'Albumin',
        totalProtein: 'Total Protein',
        inr: 'INR',
        age: 'Age (years)',
      },
      severity: 'Severity',
      severityLevels: {
        normal: 'Normal',
        mild: 'Mild',
        moderate: 'Moderate',
        severe: 'Severe',
      },
      conditions: {
        normal: 'Normal Liver Function',
        fattyLiver: 'Non-Alcoholic Fatty Liver Disease',
        hepatitis: 'Chronic Hepatitis',
        acuteHepatitis: 'Acute Hepatitis',
        alcoholicLiver: 'Alcoholic Liver Disease',
        cirrhosis: 'Liver Cirrhosis',
        cholestasis: 'Cholestasis',
        biliaryObstruction: 'Biliary Obstruction',
        drugInduced: 'Drug-Induced Liver Injury',
        autoimmune: 'Autoimmune Hepatitis',
      },
      clinicalNote: 'Clinical Note',
      clinicalNoteText: 'This analysis evaluates liver function markers to identify potential conditions. Confirm with imaging studies (ultrasound/CT), viral hepatitis panel, and liver biopsy if indicated.',
    },
    thyroid: {
      title: 'Thyroid Function',
      subtitle: 'Analyze thyroid function using TSH, T3, and T4 values to identify thyroid disorders',
      inputTitle: 'Enter Thyroid Function Parameters',
      coreParams: 'Core Parameters',
      freeHormones: 'Free Hormones',
      antibodies: 'Thyroid Antibodies',
      additional: 'Additional Information',
      fields: {
        tsh: 'TSH',
        t3: 'Total T3',
        t4: 'Total T4',
        freeT3: 'Free T3',
        freeT4: 'Free T4',
        tpoAb: 'TPO Antibodies',
        tgAb: 'Thyroglobulin Ab',
        tsi: 'TSI',
        age: 'Age (years)',
      },
      severity: 'Severity',
      severityLevels: {
        normal: 'Normal',
        mild: 'Mild',
        moderate: 'Moderate',
        severe: 'Severe',
      },
      conditions: {
        normal: 'Normal Thyroid Function',
        primaryHypo: 'Primary Hypothyroidism',
        subclinicalHypo: 'Subclinical Hypothyroidism',
        centralHypo: 'Central Hypothyroidism',
        primaryHyper: 'Primary Hyperthyroidism',
        subclinicalHyper: 'Subclinical Hyperthyroidism',
        hashimoto: 'Hashimoto\'s Thyroiditis',
        graves: 'Graves\' Disease',
        thyroiditis: 'Thyroiditis',
        toxicNodule: 'Toxic Nodular Goiter',
        sickEuthyroid: 'Sick Euthyroid Syndrome',
        drugInduced: 'Drug-Induced Thyroid Disorder',
      },
      clinicalNote: 'Clinical Note',
      clinicalNoteText: 'This prediction analyzes thyroid hormone levels and antibodies to identify potential thyroid disorders. Confirm with thyroid ultrasound, radioactive iodine uptake scan, and clinical correlation for definitive diagnosis.',
    },
    lipid: {
      title: 'Lipid Profile',
      subtitle: 'Analyze cholesterol and lipid levels to assess cardiovascular risk',
      inputTitle: 'Enter Lipid Panel Values',
      coreLipids: 'Core Lipid Panel',
      additionalMarkers: 'Additional Markers',
      advancedMarkers: 'Advanced Markers',
      fields: {
        totalCholesterol: 'Total Cholesterol',
        ldl: 'LDL Cholesterol',
        hdl: 'HDL Cholesterol',
        triglycerides: 'Triglycerides',
        vldl: 'VLDL',
        nonHdl: 'Non-HDL Cholesterol',
        apoB: 'Apolipoprotein B',
        lpA: 'Lipoprotein(a)',
        age: 'Age (years)',
      },
      cardiovascularRisk: 'Cardiovascular Risk',
      riskScore: 'Risk Score',
      riskLevels: {
        low: 'Low Risk',
        moderate: 'Moderate Risk',
        high: 'High Risk',
        very_high: 'Very High Risk',
      },
      calculatedRatios: 'Calculated Ratios',
      ratios: {
        tcHdl: 'TC/HDL',
        ldlHdl: 'LDL/HDL',
        tgHdl: 'TG/HDL',
      },
      parameterResults: 'Lipid Parameter Analysis',
      target: 'Target',
      status: {
        optimal: 'Optimal',
        borderline: 'Borderline',
        high: 'High',
        low: 'Low',
      },
      clinicalNote: 'Clinical Note',
      clinicalNoteText: 'This lipid profile analysis evaluates cardiovascular risk based on cholesterol levels and ratios. Lifestyle modifications and statin therapy may be recommended based on overall risk assessment.',
    },
    urinalysis: {
      title: 'Urinalysis',
      subtitle: 'Comprehensive urine analysis for kidney, metabolic, and urinary tract assessment',
      inputTitle: 'Enter Urinalysis Values',
      physical: 'Physical Properties',
      chemical: 'Chemical Tests',
      infection: 'Infection Markers',
      microscopic: 'Microscopic Examination',
      fields: {
        color: 'Color',
        clarity: 'Clarity',
        specificGravity: 'Specific Gravity',
        ph: 'pH',
        protein: 'Protein',
        glucose: 'Glucose',
        ketones: 'Ketones',
        blood: 'Blood',
        leukocytes: 'Leukocyte Esterase',
        nitrites: 'Nitrites',
        bilirubin: 'Bilirubin',
        urobilinogen: 'Urobilinogen',
        rbc: 'RBC',
        wbc: 'WBC',
        bacteria: 'Bacteria',
        casts: 'Casts',
      },
      options: {
        color: {
          pale: 'Pale Yellow',
          yellow: 'Yellow',
          dark_yellow: 'Dark Yellow',
          amber: 'Amber',
          red: 'Red/Pink',
          brown: 'Brown',
        },
        clarity: {
          clear: 'Clear',
          slightly_cloudy: 'Slightly Cloudy',
          cloudy: 'Cloudy',
          turbid: 'Turbid',
        },
        qualitative: {
          negative: 'Negative',
          trace: 'Trace',
          '1plus': '1+',
          '2plus': '2+',
          '3plus': '3+',
          positive: 'Positive',
        },
        urobilinogen: {
          normal: 'Normal',
          elevated: 'Elevated',
        },
      },
      refs: {
        color: 'Pale to Yellow',
        clarity: 'Clear',
        negative: 'Negative',
        urobilinogen: 'Normal (0.2-1.0)',
      },
      parameterResults: 'Parameter Analysis',
      conditions: {
        normal: 'Normal Urinalysis',
        uti: 'Urinary Tract Infection',
        proteinuria: 'Proteinuria',
        glucosuria: 'Glucosuria',
        hematuria: 'Hematuria',
        ketonuria: 'Ketonuria',
        bilirubinuria: 'Bilirubinuria',
      },
      clinicalNote: 'Clinical Note',
      clinicalNoteText: 'This urinalysis interpretation identifies potential urinary tract, kidney, or metabolic conditions. Abnormal findings should be confirmed with urine culture, microscopy, and additional tests as indicated.',
    },
  },
};
